import type { TFunction } from 'i18next';

import { ProjectApplicationTypeEnum } from '../validation/enums';
import {
  CreateEmailResendDto,
  CreateInvoiceToContractorEmailDto,
  DefaultApi,
  EmailPreviewDto,
  GetEmailDto,
} from '../autogenerated/pokApiClient';
import { validateAgainst } from '../validation/validateAgainst';
import { EmailResendSchema, EmailSchema } from '../validation/schemas';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { emailForGrid } from '../../../pok/components/Emails/Emails';
import { TKeys } from '../../hooks/useTranslation';

export interface EmailContextInterface {
  sendOrderEmail: (id: string) => Promise<GetEmailDto>;
  sendOrderExecutionEmail: (id: string) => Promise<GetEmailDto>;
  sendApplicationEmail: (id: string) => Promise<GetEmailDto>;
  sendApplicationExecutionEmail: (id: string) => Promise<GetEmailDto>;
  sendProjectEmail: (
    id: string,
    type: ProjectApplicationTypeEnum,
  ) => Promise<void>;
  sendProjectResponseEmail: (
    id: string,
    type: ProjectApplicationTypeEnum,
    accepted: boolean,
  ) => Promise<GetEmailDto>;
  sendUnblockEmail: (id: string) => Promise<GetEmailDto>;
  sendPurchaseDeficienciesEmail: (
    tv: boolean,
    email: string[],
    message: string,
    teamId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    selectedId?: string[],
  ) => Promise<GetEmailDto>;
  sendSalesInvoicingEmail: (
    email: string[],
    message: string,
    teamId?: string,
    invoiced?: boolean,
    month?: Date,
  ) => Promise<GetEmailDto>;
  sendSpecialInvoiceEmail: (
    id: string,
    correction?: boolean,
  ) => Promise<GetEmailDto>;
  sendInvoiceToContractorFromParams: (
    email: CreateInvoiceToContractorEmailDto,
  ) => Promise<GetEmailDto>;
  getById: (id: string, withCompanyLogo?: boolean) => Promise<GetEmailDto>;
  getAllByPortion: (
    t: TFunction<'applicationLng', 'applicationLng'>,
    tk: TKeys<'applicationLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    onlyNotSent?: boolean,
    setRefresh?: () => void,
    setLoading?: (loading: boolean) => void,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    onlyNotSent?: boolean,
  ) => Promise<number>;
  resend: (email: CreateEmailResendDto) => Promise<GetEmailDto>;
  preview: (id: string) => Promise<EmailPreviewDto>;
  getHistory: (id: string) => Promise<GetEmailDto[]>;
}

export const EmailContext = (api: DefaultApi) => {
  return {
    sendOrderEmail: (id: string) => api.emailControllerSendOrderEmail(id),
    sendOrderExecutionEmail: (id: string) =>
      api.emailControllerSendOrderExecutionEmail(id),
    sendApplicationEmail: (id: string) =>
      api.emailControllerSendApplicationEmail(id),
    sendApplicationExecutionEmail: (id: string) =>
      api.emailControllerSendApplicationExecutionEmail(id),
    sendProjectEmail: (id: string, type: ProjectApplicationTypeEnum) =>
      api.emailControllerSendProjectEmail(id, type),
    sendProjectResponseEmail: (
      id: string,
      type: ProjectApplicationTypeEnum,
      accepted: boolean,
    ) => api.emailControllerSendProjectResponseEmail(id, type, accepted),
    sendUnblockEmail: (id: string) => api.emailControllerSendUnblockEmail(id),
    sendPurchaseDeficienciesEmail: (
      tv: boolean,
      email: string[],
      message: string,
      teamId?: string,
      dateFrom?: Date,
      dateTo?: Date,
      selectedIds?: string[],
    ) =>
      api.emailControllerSendPurchaseDeficienciesEmail(
        tv,
        email,
        message,
        teamId,
        dateFrom,
        dateTo,
        selectedIds,
      ),
    sendSalesInvoicingEmail: (
      email: string[],
      message: string,
      teamId?: string,
      invoiced?: boolean,
      month?: Date,
    ) =>
      api.emailControllerSendSalesInvoicingEmail(
        email,
        message,
        teamId,
        invoiced,
        month,
      ),
    sendSpecialInvoiceEmail: (id: string, correction?: boolean) =>
      api.emailControllerSendSpecialInvoiceEmail(id, correction),
    getById: (id: string, withCompanyLogo?: boolean) =>
      api.emailControllerGetById(id, withCompanyLogo),
    sendInvoiceToContractorFromParams: (
      email: CreateInvoiceToContractorEmailDto,
    ) => api.emailControllerSendInvoiceToContractorFromParams(email),
    getAllByPortion: async (
      t: TFunction<'applicationLng', 'applicationLng'>,
      tk: TKeys<'applicationLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      onlyNotSent = true,
      setRefresh?: () => void,
      setLoading?: (loading: boolean) => void,
    ) => {
      const data = await api.emailControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        onlyNotSent,
        filterColumns || '',
      );
      return data.map(email =>
        emailForGrid(email, t, tk, setRefresh, setLoading),
      );
    },
    getAllCount: (
      filterText?: string,
      filterColumns?: string,
      onlyNotSent = true,
    ) =>
      api.emailControllerGetAllCount(
        filterText || '',
        onlyNotSent,
        filterColumns || '',
      ),
    resend: (email: CreateEmailResendDto) =>
      api.emailControllerResendEmail(email),
    preview: (id: string) => api.emailControllerPreview(id),
    getHistory: (id: string) => api.emailControllerGetEmailHistory(id),
  };
};

export const validate = (
  emailToContractor: CreateInvoiceToContractorEmailDto,
) => validateAgainst(EmailSchema, emailToContractor);

export const validateResend = (emailAddress: CreateEmailResendDto) =>
  validateAgainst(EmailResendSchema, emailAddress);
